import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight, Heart, ImagesSquare, CalendarBlank, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  listChildrenForParent,
  listReportsForChild,
} from "@/lib/dailyReports";
import { listPhotosForChildren } from "@/lib/photos";
import { isSupabaseConfigured } from "@/lib/supabase";
import PhotoTile from "@/components/photos/PhotoTile";
import PhotoLightbox from "@/components/photos/PhotoLightbox";
import { ENTRY_META, prettyDate } from "@/lib/entryTypes";
import { listFoodMealUpdatesForChild, mergeParentActivityDays } from "@/lib/foodProgram";
import { useOrganizationConfig } from "@/context/OrganizationConfigContext";
import { listParentLearningObservations } from "@/lib/learningObservations";

export default function ParentActivity() {
  const { profile } = useAuth();
  const { isModuleEnabled } = useOrganizationConfig();
  const learningEnabled = isModuleEnabled("learning");
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [reports, setReports] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [learningObservations, setLearningObservations] = useState([]);
  const [learningLoading, setLearningLoading] = useState(false);
  const [learningError, setLearningError] = useState("");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("days"); // 'days' | 'photos' | 'growth'
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const activityTabRefs = useRef({});
  const activityModes = useMemo(
    () => (learningEnabled ? ["days", "photos", "growth"] : ["days", "photos"]),
    [learningEnabled],
  );

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      try {
        const kids = await listChildrenForParent(profile.id);
        setChildren(kids);
        setActiveChildId(kids[0]?.id || null);
      } catch (err) {
        toast.error(err.message || "Could not load your children");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id]);

  useEffect(() => {
    if (!activeChildId) return;
    (async () => {
      try {
        const [rows, foodEntries, photoResult] = await Promise.all([
          listReportsForChild(activeChildId, 30),
          listFoodMealUpdatesForChild(activeChildId, { limit: 240 }),
          listPhotosForChildren([activeChildId]).then(
            (data) => ({ data, error: null }),
            (error) => ({ data: [], error }),
          ),
        ]);
        setReports(
          mergeParentActivityDays(
            rows,
            foodEntries.map((entry) => ({ ...entry, child_id: activeChildId })),
          ),
        );
        setPhotos(photoResult.data);
        if (photoResult.error && isSupabaseConfigured) {
          toast.error("Activity is available, but photos could not be loaded.");
        }
      } catch (err) {
        toast.error(err.message || "Could not load activity");
      }
    })();
  }, [activeChildId]);

  const loadLearning = useCallback(async () => {
    if (!activeChildId || !learningEnabled) {
      setLearningObservations([]);
      setLearningError("");
      setLearningLoading(false);
      return;
    }
    setLearningLoading(true);
    setLearningError("");
    try {
      setLearningObservations(await listParentLearningObservations({ childId: activeChildId, limit: 30 }));
    } catch (error) {
      setLearningError(error?.message || "Growth updates could not be loaded.");
    } finally {
      setLearningLoading(false);
    }
  }, [activeChildId, learningEnabled]);

  useEffect(() => {
    loadLearning();
  }, [loadLearning]);

  useEffect(() => {
    if (!learningEnabled && mode === "growth") setMode("days");
  }, [learningEnabled, mode]);

  // Keep the compact activity switcher a real tabs pattern for keyboard and
  // assistive-technology users, not only a row of visually styled buttons.
  const moveActivityTab = useCallback((event, currentMode) => {
    let nextMode = null;
    const currentIndex = activityModes.indexOf(currentMode);
    if (event.key === "ArrowRight") {
      nextMode = activityModes[(currentIndex + 1) % activityModes.length];
    } else if (event.key === "ArrowLeft") {
      nextMode = activityModes[(currentIndex - 1 + activityModes.length) % activityModes.length];
    } else if (event.key === "Home") {
      nextMode = activityModes[0];
    } else if (event.key === "End") {
      nextMode = activityModes[activityModes.length - 1];
    }
    if (!nextMode) return;
    event.preventDefault();
    setMode(nextMode);
    requestAnimationFrame(() => activityTabRefs.current[nextMode]?.focus());
  }, [activityModes]);

  const activeChild = useMemo(
    () => children.find((c) => c.id === activeChildId),
    [children, activeChildId],
  );

  const childrenById = useMemo(
    () => Object.fromEntries(children.map((c) => [c.id, c])),
    [children],
  );

  if (loading) {
    return (
      <div
        data-testid="parent-activity-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div
        data-testid="parent-activity-no-children"
        className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-neutral-500"
      >
        No children are linked to your account yet. Please contact your daycare admin.
      </div>
    );
  }

  return (
    <div data-testid="parent-activity" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Activity
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          {activeChild ? `${activeChild.first_name}'s days` : "Recent days"}
        </h1>
      </section>

      {children.length > 1 && (
        <div
          data-testid="parent-child-switcher"
          className="flex gap-2 overflow-x-auto pb-1"
        >
          {children.map((c) => (
            <button
              key={c.id}
              data-testid={`parent-child-tab-${c.id}`}
              onClick={() => setActiveChildId(c.id)}
              className={`flex-shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                activeChildId === c.id
                  ? "border-primary bg-primary/10 text-primary-hover"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {/* Disambiguate same-name siblings with a last initial. */}
              {children.some((o) => o.id !== c.id && o.first_name === c.first_name) && c.last_name
                ? `${c.first_name} ${c.last_name[0]}.`
                : c.first_name}
            </button>
          ))}
        </div>
      )}

      <div
        data-testid="parent-activity-mode-switcher"
        role="tablist"
        aria-label="Activity views"
        className="inline-flex rounded-full border border-neutral-200 bg-white p-0.5"
      >
        <button
          type="button"
          id="parent-activity-tab-days"
          role="tab"
          aria-selected={mode === "days"}
          aria-controls="parent-activity-mode-panel"
          data-testid="parent-activity-mode-days"
          data-active={mode === "days"}
          onClick={() => setMode("days")}
          onKeyDown={(event) => moveActivityTab(event, "days")}
          ref={(element) => { activityTabRefs.current.days = element; }}
          tabIndex={mode === "days" ? 0 : -1}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${
            mode === "days"
              ? "bg-primary text-white"
              : "text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          <CalendarBlank size={14} weight="duotone" />
          Days
        </button>
        <button
          type="button"
          id="parent-activity-tab-photos"
          role="tab"
          aria-selected={mode === "photos"}
          aria-controls="parent-activity-mode-panel"
          data-testid="parent-activity-mode-photos"
          data-active={mode === "photos"}
          onClick={() => setMode("photos")}
          onKeyDown={(event) => moveActivityTab(event, "photos")}
          ref={(element) => { activityTabRefs.current.photos = element; }}
          tabIndex={mode === "photos" ? 0 : -1}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${
            mode === "photos"
              ? "bg-primary text-white"
              : "text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          <ImagesSquare size={14} weight="duotone" />
          Photos
          {photos.length > 0 && (
            <span
              data-testid="parent-activity-photos-count"
              className={`rounded-full px-1.5 text-[10px] font-bold ${
                mode === "photos" ? "bg-white/20" : "bg-neutral-100 text-neutral-600"
              }`}
            >
              {photos.length}
            </span>
          )}
        </button>
        {learningEnabled ? (
          <button
            type="button"
            id="parent-activity-tab-growth"
            role="tab"
            aria-selected={mode === "growth"}
            aria-controls="parent-activity-mode-panel"
            data-testid="parent-activity-mode-growth"
            data-active={mode === "growth"}
            onClick={() => setMode("growth")}
            onKeyDown={(event) => moveActivityTab(event, "growth")}
            ref={(element) => { activityTabRefs.current.growth = element; }}
            tabIndex={mode === "growth" ? 0 : -1}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${
              mode === "growth"
                ? "bg-primary text-white"
                : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            <Sparkle size={14} weight="duotone" />
            Growth
            {learningObservations.length > 0 && (
              <span className={`rounded-full px-1.5 text-[10px] font-bold ${mode === "growth" ? "bg-white/20" : "bg-neutral-100 text-neutral-600"}`}>
                {learningObservations.length}
              </span>
            )}
          </button>
        ) : null}
      </div>

      <section id="parent-activity-mode-panel" role="tabpanel" aria-labelledby={`parent-activity-tab-${mode}`}>
      {mode === "photos" ? (
        photos.length === 0 ? (
          <div
            data-testid="parent-photos-empty"
            className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
              <ImagesSquare size={20} weight="duotone" className="text-primary-hover" />
            </div>
            <div>
              <p className="font-heading text-base font-semibold text-neutral-900">
                No photos yet
              </p>
              <p className="mt-1 text-sm text-neutral-500">
                When your child's teacher adds a photo, you'll see it right here.
              </p>
            </div>
          </div>
        ) : (
          <div
            data-testid="parent-photos-gallery"
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
          >
            {photos.map((p, i) => (
              <PhotoTile
                key={p.id}
                photo={p}
                caption
                data-testid={`parent-photo-${p.id}`}
                onOpen={() => setLightboxIndex(i)}
              />
            ))}
          </div>
        )
      ) : mode === "growth" ? (
        learningLoading ? (
          <div data-testid="parent-learning-loading" className="flex items-center justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div>
        ) : learningError ? (
          <div data-testid="parent-learning-error" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            <p>{learningError}</p>
            <button type="button" onClick={loadLearning} className="mt-3 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100">Try again</button>
          </div>
        ) : learningObservations.length === 0 ? (
          <div data-testid="parent-learning-empty" className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><Sparkle size={20} weight="duotone" className="text-primary-hover" /></div>
            <div>
              <p className="font-heading text-base font-semibold text-neutral-900">Nothing shared yet</p>
              <p className="mt-1 text-sm text-neutral-500">When your center shares a learning moment about {activeChild?.first_name || "your child"}, it will appear here.</p>
            </div>
          </div>
        ) : (
          <ul data-testid="parent-learning-list" className="space-y-3">
            {learningObservations.map((observation) => (
              <li key={observation.id} className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {observation.focusLabel ? <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary-hover">{observation.focusLabel}</span> : <span className="text-xs font-semibold uppercase tracking-[0.15em] text-primary-hover">Growth moment</span>}
                  <span className="text-xs text-neutral-500">{formatLearningDate(observation.observedAt)}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-neutral-700">{observation.note}</p>
              </li>
            ))}
          </ul>
        )
      ) : reports.length === 0 ? (
        <div
          data-testid="parent-activity-empty"
          className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
            <Heart size={20} weight="duotone" className="text-primary-hover" />
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-neutral-900">
              No activity yet
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              When your child’s day is logged, including food service, it will show up here.
            </p>
          </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="parent-activity-list">
          {reports.map((r) => {
            const counts = countsByType(r.entries);
            return (
              <li key={r.id}>
                <Link
                  to={`/parent/activity/${r.report_date}?child=${r.child_id}`}
                  data-testid={`activity-day-${r.report_date}`}
                  className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                      {prettyDate(r.report_date)}
                    </p>
                    {r.overall_mood && (
                      <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-primary-hover">
                        Mood · {r.overall_mood}
                      </p>
                    )}
                    {r.summary_note && (
                      <p className="mt-1.5 line-clamp-2 text-sm text-neutral-600">
                        {r.summary_note}
                      </p>
                    )}
                    {counts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {counts.map(([type, n]) => {
                          const meta = ENTRY_META[type];
                          const Icon = meta.icon;
                          const labelPlural = pluralise(meta.label, n);
                          return (
                            <span
                              key={type}
                              className={`inline-flex items-center gap-1 rounded-full ${meta.bg} px-2.5 py-0.5 text-[11px] font-medium ${meta.color}`}
                            >
                              <Icon size={12} weight="duotone" />
                              {n} {labelPlural.toLowerCase()}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <CaretRight size={18} weight="bold" className="mt-1 text-neutral-400" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      </section>

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          childrenById={childrenById}
          canReport
          onReported={(photoId) => {
            // RLS hides flagged photos from parents, so drop it locally
            // for instant feedback without a re-fetch.
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
          }}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function formatLearningDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function countsByType(entries = []) {
  const m = new Map();
  entries.forEach((e) => m.set(e.entry_type, (m.get(e.entry_type) || 0) + 1));
  return Array.from(m.entries());
}

function pluralise(label, n) {
  if (n === 1) return label;
  if (label === "Activity") return "Activities";
  return `${label}s`;
}
